import Button from 'components/base/Button';
import { Col, Form, Row, Modal } from 'react-bootstrap';
import ModalForm from 'components/forms/ModalForm';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { CREATE_RIG, RigDTO, UPDATE_RIG } from 'services/api/rigs';
import { FormProps } from 'data/from-data/interface/modal-form-props.interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';

export class RigFormData {
  [key: string]: FormProps;

  name: FormProps = {
    name: 'name',
    label: 'Name',
    type: 'text',
    placeholder: 'Name',
    required: true
  };
  rigType: FormProps = {
    name: 'rigType',
    label: 'Rig Type',
    type: 'select',
    placeholder: 'Select Rig Type',
    required: true,
    options: [
      { value: 'Drill Ship', label: 'Drill Ship' },
      { value: 'Jack-Up', label: 'Jack-Up' },
      { value: 'Floater', label: 'Floater' },
      { value: 'Semi-Submersible', label: 'Semi-Submersible' },
      { value: 'Platform', label: 'Platform' },
      { value: 'Work-over', label: 'Work-over' },
      { value: 'Land-Rig Single', label: 'Land-Rig Single' },
      { value: 'Land-Rig Double', label: 'Land-Rig Double' },
      { value: 'Land-Rig Triple', label: 'Land-Rig Triple' }
    ]
  };
}

interface RigModalProps {
  show: boolean;
  handleClose: () => void;
  isEditing?: boolean;
  rig?: any;
  onSuccess?: () => void;
}

const RigModal = ({
  show,
  handleClose,
  isEditing = false,
  rig,
  onSuccess
}: RigModalProps) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [createRigDto, setRigDto] = useState<RigFormData>(new RigFormData());
  const [isLoading, setLoading] = useState<boolean>(false);

  const query = useQueryClient();

  useEffect(() => {
    if (rig && isEditing) {
      setRigDto(prevData => {
        const updatedData = { ...prevData };

        if (rig.name) {
          updatedData.name = {
            ...prevData.name,
            value: rig.name
          };
        }

        if (rig.description) {
          updatedData.rigType = {
            ...prevData.rigType,
            value: rig.description
          };
        }

        return updatedData;
      });
    }
  }, [rig, isEditing]);

  const createRigAction = useMutation('createRig', CREATE_RIG, {
    onError: (err: any) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'Failed to create rig');
    },
    onSuccess: () => {
      setLoading(false);
      query.invalidateQueries('get_rigs');
      setRigDto(new RigFormData());
      handleClose();
      onSuccess?.();
      setValidated(false);
      toast.success('Rig created successfully');
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  const updateRigAction = useMutation('createRig', UPDATE_RIG, {
    onError: (err: any) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'Failed to update rig');
    },
    onSuccess: () => {
      setLoading(false);
      query.invalidateQueries('get_rigs');
      handleClose();
      onSuccess?.();
      toast.success('Rig updated successfully');
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!createRigDto.name.value) {
      event.stopPropagation();
      setValidated(true);
    } else {
      handleClose();
      return runFormSubmissionAction(event);
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setRigDto(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    const {
      name: { value: name },
      rigType: { value: rigType }
    } = createRigDto;
    const payload: RigDTO = {
      name,
      description: rigType
    };

    isEditing
      ? await updateRigAction.mutateAsync({
          rigId: (rig as any)?.id || rig?._id,
          data: payload
        })
      : await createRigAction.mutateAsync(payload);
  };
  const renderFormFields = () => {
    return Object.entries(createRigDto).map(([key, formProps]) => (
      <Col md="12" key={key}>
        <ModalForm
          formProps={formProps as unknown as FormProps}
          handleInputChange={handleInputChange}
        />
      </Col>
    ));
  };
  return (
    <Modal show={show} onHide={handleClose} className="p-0" centered>
      <Modal.Header className="border-200 p-4">
        <h5 className="modal-title text-1000 fs-6 lh-sm">
          {isEditing ? 'Edit Rig' : 'Create New Rig'}
        </h5>
        <Button className="p-1" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} className="fs-9" />
        </Button>
      </Modal.Header>
      <Modal.Body className="pb-2 px-4 mb-5">
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row className="g-3">{renderFormFields()}</Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
        <Button
          variant="phoenix-primary"
          size="sm"
          className="px-4 fs-10 my-0"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="px-9 fs-9 my-0"
          disabled={isLoading}
          type="submit"
          onClick={handleSubmit}
        >
          {isEditing ? 'Save Changes' : 'Create Rig'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RigModal;
